import { BaseUserProfile, bodyComposition, Gender, ProgramType } from 'fitbeat.models';
import { UserProfile } from '../../entities';
import { monthsToDaysCoefficient, round, roundWhole } from '../../logic/analytics';
import { WorkoutTargetConfig } from '../../logic/analytics/WorkoutTargetConfig';
import { calculateCaloriesPerMinuteAverage } from '../../metrics/metric-calculator';

export class BodyProgressModel {
    private assumedMonthlyCalorieDeficit: {
        [type in ProgramType]: {
        [gender in Gender]: number;
        };
      } = {
        [ProgramType.GetLeanAndFitGoal]: {
          [Gender.Male]: 10000,
          [Gender.Female]: 7000,
          [Gender.Other]: 7000,
        },
        [ProgramType.GainMuscleAndStrengthGoal]: {
          [Gender.Male]: 0,
          [Gender.Female]: 0,
          [Gender.Other]: 0,
        },
        [ProgramType.GetStrongAndFitGoal]: {
          [Gender.Male]: 0,
          [Gender.Female]: 0,
          [Gender.Other]: 0,
        },
        [ProgramType.MobilityAndTone]: {
          [Gender.Male]: 0,
          [Gender.Female]: 0,
          [Gender.Other]: 0,
        },
        [ProgramType.MuscleAndShred]: {
          [Gender.Male]: 10000,
          [Gender.Female]: 7000,
          [Gender.Other]: 7000,
        },
        [ProgramType.NotSet]: {
          [Gender.Male]: 0,
          [Gender.Female]: 0,
          [Gender.Other]: 0,
        },
      };

    private assumedMonthlyMuscleMassIncrease: {
      [bodyComp in bodyComposition]: number
    } = {
      [bodyComposition.ecto]: 0.5,
      [bodyComposition.ecto_meso]: 0.75,
      [bodyComposition.meso]: 1.0,
      [bodyComposition.endo_meso]: 0.75,
      [bodyComposition.endo]: 0.75,
    };

    constructor(
        private user: UserProfile,
        private targetWeeklyMinutes: number | undefined,
    ) { }
    public calculateAssumedWeeklyWorkoutCalorieExpenditure() {

        const hrCoefficients = {
            [ProgramType.GainMuscleAndStrengthGoal]: 0.6,
            [ProgramType.GetLeanAndFitGoal]: 0.8,
            [ProgramType.GetStrongAndFitGoal]: 0.7,
            [ProgramType.MobilityAndTone]: 0.7,
            [ProgramType.MuscleAndShred]: 0.6,
            [ProgramType.NotSet]: 0,
        };
        const hrMax = BaseUserProfile.calculateHeartRateMax(this.user.age);
        const hrAgeMax = hrMax * hrCoefficients[this.user.longTermGoal];

        const calPerMin = calculateCaloriesPerMinuteAverage(
          this.user.gender,
          hrAgeMax,
          this.user.weight,
          this.user.age,
        );
        const targetWeeklyWorkoutMinutes = this.targetWeeklyMinutes ?
            this.targetWeeklyMinutes : WorkoutTargetConfig[this.user.longTermGoal];
        const weeklyCalories = calPerMin * targetWeeklyWorkoutMinutes;

        return roundWhole(weeklyCalories);
    }

    public calculateTotalDailyDeficit() {
        const dailyCalorieDeficitFromNutritionOnly = this.calculateDailyCalorieDeficitFromNutritionOnly();
        const dailyCalorieExpenditure = this.calculateDailyCalorieExpenditure();
        const totalDailyDeficit = dailyCalorieDeficitFromNutritionOnly + dailyCalorieExpenditure;

        return round(totalDailyDeficit, 100);
    }

    public calculateMonthlyLossOrGain() {
        const caloriesInOneKg = 7700;

        const totalDailyDeficit = this.calculateTotalDailyDeficit();
        const dailyLossOrGainKg = (totalDailyDeficit / caloriesInOneKg);

        const weeklyLossOrGainKg = dailyLossOrGainKg * monthsToDaysCoefficient;
        const rootWeeklyLossOrGainKg = -weeklyLossOrGainKg;

        return round(rootWeeklyLossOrGainKg, 10);
    }

    public calculateAssumedDailyLossInMass() {
        const caloriesInOneKg = 7700;
        const totalDailyDeficit = this.calculateTotalDailyDeficit();
        return totalDailyDeficit / caloriesInOneKg;
    }

    public calculateAssumedDailyGainInMuscleMass() {
      if (!this.user.bodyComposition) {
        throw new Error(`User bodyComposition is needed for muscle mass target.`);
      }

      const monthlyAssumedGainInMuscleMass =
        this.assumedMonthlyMuscleMassIncrease[
          bodyComposition[this.user.bodyComposition]
        ];
      return monthlyAssumedGainInMuscleMass / monthsToDaysCoefficient;
    }

    private calculateDailyCalorieDeficitFromNutritionOnly() {
      const monthlyCaloryDeficit = this.assumedMonthlyCalorieDeficit[this.user.longTermGoal][this.user.gender];
      const averageDaysInMonth = monthsToDaysCoefficient;
      const dailyCalorieDeficit = monthlyCaloryDeficit / averageDaysInMonth;
      return dailyCalorieDeficit;
    }

    private calculateDailyCalorieExpenditure() {
        const weeklyWorkoutExpenditure = this.calculateAssumedWeeklyWorkoutCalorieExpenditure();
        const dailyWorkoutExpenditure = weeklyWorkoutExpenditure / 7;
        return dailyWorkoutExpenditure;
    }
}
